import React, { useState, useEffect } from 'react';
import { X, Crown, Check, Lightning, Star, Gift, Tag, Ticket } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { isMandatePlan, openMandateCheckout } from '../lib/premiumCheckout';
import { OfferCountdown } from './OfferCountdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = 'rzp_live_OPQaGpWtnwCBjC';

// Promotion end date - March 1, 2026 11:59 PM IST
const PROMO_END_DATE = new Date('2026-03-01T23:59:59+05:30');

const PricingModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [processing, setProcessing] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isPromoActive, setIsPromoActive] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, discount_type, discount_value, discount_amount, referrer_name}
  const [discountError, setDiscountError] = useState('');

  // Limited-time launch offer (25% off first payment)
  const [launchOffer, setLaunchOffer] = useState(null);
  useEffect(() => {
    let ok = true;
    fetch(`${API_URL}/api/premium-plan/launch-offer`)
      .then((r) => r.json())
      .then((d) => { if (ok) setLaunchOffer(d); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);
  const offerOneTime = !!launchOffer?.active && (launchOffer?.mode || 'one_time') === 'one_time';
  const offerActive = !!launchOffer?.active && (offerOneTime || Object.values(launchOffer?.plans || {}).some((p) => p.configured));
  const offerPct = launchOffer?.discount_percent || 25;
  const offerEndsLabel = (() => {
    try { return new Date(launchOffer.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
    catch { return null; }
  })();

  // Countdown timer for promotion
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const diff = PROMO_END_DATE - now;
      
      if (diff > 0) {
        setIsPromoActive(true);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft({ hours, minutes, seconds });
      } else {
        setIsPromoActive(false);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch subscription details - simplified (no proration)
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (!user?.id || !isOpen) return;
      
      try {
        const response = await fetch(`${API_URL}/api/subscription/status?user_id=${user.id}`);
        const data = await response.json();
        
        if (data.is_premium && data.subscription_end) {
          const endDate = new Date(data.subscription_end);
          const now = new Date();
          const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
          
          setSubscriptionDetails({
            currentPlan: data.plan || data.plan_name,
            daysRemaining,
            endDate: endDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
          });
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      }
    };
    
    fetchSubscriptionDetails();
  }, [user?.id, isOpen]);

  // Normalize currentPlan to match our plan IDs
  // Backend might send "premium_monthly" or "monthly", etc.
  // Double reports during promotion
  const getReportCount = (baseCount) => isPromoActive ? baseCount * 2 : baseCount;

  const plans = [
    {
      id: 'lite_monthly',
      name: 'Lite',
      price: 527,
      displayPrice: '527',
      period: '/month',
      duration: 1,
      badge: 'TRY PREMIUM',
      badgeColor: 'bg-sky-500',
      features: [
        { text: `${getReportCount(2)} Unified Report Credits`, highlight: true, promo: isPromoActive },
        { text: 'Use across Astro · Vastu · Numerology', highlight: false },
        { text: 'Astro Guru AI (1,000 chats)', highlight: false },
        { text: 'Vastu Guru AI (1,000 chats)', highlight: false },
        { text: 'Occult Manager + Case Files', highlight: false },
        { text: 'Priority Support', highlight: false },
      ],
      color: 'from-sky-500 to-blue-500',
      buttonColor: 'bg-sky-500 hover:bg-sky-600',
      savings: null
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: 1400,
      displayPrice: '1,400',
      period: '/month',
      duration: 1,
      badge: null,
      features: [
        { text: `${getReportCount(15)} Unified Report Credits`, highlight: true, promo: isPromoActive },
        { text: 'Use across Astro · Vastu · Numerology', highlight: false },
        { text: 'Astro Guru AI (5,000 chats)', highlight: false },
        { text: 'Vastu Guru AI (5,000 chats)', highlight: false },
        { text: 'Occult Manager + Case Files', highlight: false },
        { text: 'Custom Branding on Reports', highlight: false },
        { text: 'Special Pricing on Remedy Store', highlight: false },
        { text: 'Priority Support', highlight: false },
      ],
      color: 'from-blue-500 to-blue-600',
      buttonColor: 'bg-blue-500 hover:bg-blue-600',
      savings: null
    },
    {
      id: 'half_yearly',
      name: '6 Months',
      price: 7250,
      displayPrice: '7,250',
      period: '/6 months',
      duration: 6,
      badge: 'MOST POPULAR',
      badgeColor: 'bg-amber-500',
      features: [
        { text: `${getReportCount(150)} Unified Report Credits`, highlight: true, promo: isPromoActive },
        { text: 'Use across Astro · Vastu · Numerology', highlight: false },
        { text: 'Astro Guru AI (30,000 chats)', highlight: false },
        { text: 'Vastu Guru AI (30,000 chats)', highlight: false },
        { text: 'Occult Manager + Case Files', highlight: false },
        { text: 'Custom Branding on Reports', highlight: false },
        { text: 'Special Pricing on Remedy Store', highlight: false },
        { text: 'Priority Support', highlight: false },
      ],
      color: 'from-amber-500 to-orange-500',
      buttonColor: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
      savings: 'Save Rs.1,150',
      monthlyEquivalent: 'Rs.1,208/month'
    },
    {
      id: 'yearly',
      name: '12 Months',
      price: 14000,
      displayPrice: '14,000',
      period: '/year',
      duration: 12,
      badge: 'BEST VALUE',
      badgeColor: 'bg-green-500',
      features: [
        { text: `${getReportCount(500)} Unified Report Credits`, highlight: true, promo: isPromoActive },
        { text: 'Use across Astro · Vastu · Numerology', highlight: false },
        { text: 'Astro Guru AI (60,000 chats)', highlight: false },
        { text: 'Vastu Guru AI (60,000 chats)', highlight: false },
        { text: 'Occult Manager + Case Files', highlight: false },
        { text: 'Custom Branding on Reports', highlight: false },
        { text: 'Special Pricing on Remedy Store', highlight: false },
        { text: 'Priority Support', highlight: false },
      ],
      color: 'from-green-500 to-emerald-500',
      buttonColor: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
      savings: 'Save Rs.2,800',
      monthlyEquivalent: 'Rs.1,167/month'
    }
  ];

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        console.log('[Payment] Razorpay script loaded successfully');
        resolve(true);
      };
      script.onerror = () => {
        console.error('[Payment] Failed to load Razorpay script');
        toast.error('Payment gateway failed to load. Please check your internet connection and try again.');
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const [selectedPlanForDiscount, setSelectedPlanForDiscount] = useState('monthly');

  // Validate discount code
  const validateDiscountCode = async (planId = selectedPlanForDiscount) => {
    if (!discountCode.trim()) {
      setDiscountError('');
      setAppliedDiscount(null);
      return;
    }
    
    setValidatingCode(true);
    setDiscountError('');
    
    try {
      const apiUrl = API_URL || process.env.REACT_APP_BACKEND_URL;
      if (!apiUrl) {
        console.error('[Discount] API_URL is not defined');
        setDiscountError('Configuration error. Please refresh the page.');
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/discount/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: discountCode.trim(),
          user_id: user?.id || '',
          plan_id: planId
        })
      });
      
      if (!response.ok) {
        console.error('[Discount] API error:', response.status, response.statusText);
        setDiscountError(`Server error (${response.status}). Please try again.`);
        setAppliedDiscount(null);
        return;
      }
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedDiscount(data);
        setDiscountError('');
        toast.success(`Discount applied! You save ${data.discount_display}`);
      } else {
        setAppliedDiscount(null);
        setDiscountError(data.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('[Discount] Validation error:', error);
      setDiscountError('Failed to validate code. Please check your connection.');
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };
  
  // Reset discount fields then close (kept out of an effect to stay lint-clean).
  const handleClose = () => {
    setDiscountCode('');
    setAppliedDiscount(null);
    setDiscountError('');
    onClose && onClose();
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.error('Please login to subscribe');
      return;
    }

    setProcessing(plan.id);
    console.log('[Payment] Starting payment for plan:', plan.id, 'discount:', appliedDiscount?.code || 'none');

    // Auto-pay MANDATE (recurring) — but during the one-time launch offer we
    // route through the discounted one-time create-order flow instead.
    if (isMandatePlan(plan.id) && !offerOneTime) {
      await openMandateCheckout({
        plan: { id: plan.id, name: plan.name },
        user,
        discountCode: appliedDiscount?.code || null,
        onSuccess: () => {
          setDiscountCode('');
          setAppliedDiscount(null);
          setProcessing(null);
          onSuccess && onSuccess(plan);
          onClose && onClose();
        },
        onClose: () => setProcessing(null),
      });
      return;
    }

    try {
      const razorpayLoaded = await loadRazorpay();
      if (!razorpayLoaded) {
        setProcessing(null);
        return;
      }

      console.log('[Payment] Creating order for user:', user.id, 'plan:', plan.id);
      const response = await fetch(`${API_URL}/api/premium-plan/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: user.id,
          plan_id: plan.id,
          discount_code: appliedDiscount?.code || null
        })
      });

      console.log('[Payment] Order creation response status:', response.status);
      const data = await response.json();
      console.log('[Payment] Order data:', data.success ? 'Success' : 'Failed', data.error || '');

      if (data.success && data.order_id) {
        // Show proration details if upgrading
        let description = `Premium Plan - ${plan.name}`;
        if (data.is_upgrade && data.proration) {
          description = `Upgrade to ${plan.name} (₹${data.proration.proration_credit} credit applied)`;
          toast.info(`Upgrade Credit: ₹${data.proration.proration_credit} applied from your current plan!`, {
            duration: 5000
          });
        }
        
        // Show discount info
        if (data.discount_applied && data.discount) {
          description += ` - ${data.discount.code} applied`;
          toast.success(`Discount ${data.discount.code}: ₹${data.discount.discount_amount / 100} off!`, {
            duration: 5000
          });
        }
        
        console.log('[Payment] Opening Razorpay checkout, order_id:', data.order_id);
        const options = {
          key: RAZORPAY_KEY,
          order_id: data.order_id,
          amount: data.amount,
          name: "Pinnacle Vastu Solutions",
          description: description,
          prefill: {
            name: user.name || '',
            email: user.email || '',
            contact: user.phone || ''
          },
          theme: { color: '#F59E0B' },
          handler: async function(response) {
            console.log('[Payment] Payment completed, verifying...');
            try {
              const verifyResponse = await fetch(`${API_URL}/api/premium-plan/verify-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: user.id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  plan_id: plan.id,
                  discount_code: appliedDiscount?.code || null
                })
              });

              const verifyData = await verifyResponse.json();
              console.log('[Payment] Verification result:', verifyData.success ? 'Success' : 'Failed');
              if (verifyData.success) {
                if (data.is_upgrade) {
                  toast.success(`Successfully upgraded to ${plan.name}!`);
                } else {
                  toast.success('Premium subscription activated!');
                }
                // Clear discount state
                setDiscountCode('');
                setAppliedDiscount(null);
                onSuccess && onSuccess(plan);
                onClose();
              } else {
                console.error('[Payment] Verification failed:', verifyData);
                toast.error('Payment verification failed. Please contact support with your payment ID: ' + response.razorpay_payment_id);
              }
            } catch (err) {
              console.error('[Payment] Verification error:', err);
              toast.error('Error verifying payment. Please contact support.');
            }
            setProcessing(null);
          },
          modal: {
            ondismiss: function() {
              console.log('[Payment] Modal dismissed by user');
              setProcessing(null);
            },
            escape: false,
            backdropclose: false
          },
          retry: {
            enabled: true,
            max_count: 3
          }
        };

        const razorpay = new window.Razorpay(options);
        
        // Add payment failed handler
        razorpay.on('payment.failed', function(resp) {
          console.error('[Payment] Payment failed:', resp.error);
          toast.error(`Payment failed: ${resp.error.description || 'Unknown error'}. Please try again.`, {
            duration: 6000
          });
          setProcessing(null);
        });
        
        razorpay.open();
      } else {
        console.error('[Payment] Order creation failed:', data.error);
        toast.error(data.error || 'Failed to create order. Please try again.');
        setProcessing(null);
      }
    } catch (error) {
      console.error('[Payment] Subscription error:', error);
      toast.error('Network error. Please check your internet connection and try again.');
      setProcessing(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X size={24} className="text-gray-600" />
        </button>

        {/* Limited-time launch offer banner */}
        {offerActive && (
          <div data-testid="launch-offer-banner" className="px-6 py-4 text-center rounded-t-3xl" style={{ background: 'linear-gradient(105deg,#1A362D,#9C302B)' }}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="flex items-center gap-2">
                <Gift size={22} className="text-[#F5D77E] animate-pulse" weight="fill" />
                <span className="text-white font-bold text-lg">LIMITED-TIME: {offerPct}% OFF your first payment</span>
              </div>
              {launchOffer?.ends_at && (
                <OfferCountdown endsAt={launchOffer.ends_at} variant="light" />
              )}
            </div>
            <p className="text-white/80 text-sm mt-1.5">Auto-applied at checkout · then renews at the regular price. Cancel anytime.</p>
          </div>
        )}

        {/* Promotional Banner */}
        {isPromoActive && (
          <div className={`bg-gradient-to-r from-red-600 to-red-500 px-6 py-4 text-center ${!offerActive ? 'rounded-t-3xl' : ''}`}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="flex items-center gap-2">
                <Gift size={24} className="text-yellow-300 animate-pulse" weight="fill" />
                <span className="text-white font-bold text-lg">DOUBLE REPORTS OFFER!</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <span className="text-white font-mono font-bold">
                  {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
                </span>
                <span className="text-white/80 text-sm">left</span>
              </div>
            </div>
            <p className="text-red-100 text-sm mt-2">Subscribe by March 1, 11:59 PM to get 2X report credits!</p>
          </div>
        )}

        <div className={`bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-10 text-center ${!isPromoActive && !offerActive ? 'rounded-t-3xl' : ''}`}>
          <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Crown size={20} weight="fill" />
            PREMIUM PLANS
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {subscriptionDetails ? 'Extend Your Premium' : 'Choose Your Perfect Plan'}
          </h2>
          <p className="text-amber-100 text-lg">
            Unlock the full power of Vastu and Astrology guidance
          </p>
          {subscriptionDetails && subscriptionDetails.daysRemaining > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-white/30 text-white px-4 py-2 rounded-full text-sm font-semibold">
              <Star size={16} weight="fill" />
              {subscriptionDetails.daysRemaining} days remaining (expires {subscriptionDetails.endDate})
            </div>
          )}
        </div>

        {/* Simple Info Banner - Days will be added */}
        {subscriptionDetails && subscriptionDetails.daysRemaining > 0 && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
            <div className="flex items-center justify-center gap-3 text-white">
              <div className="bg-white/20 p-2 rounded-full">
                <Lightning size={24} weight="fill" />
              </div>
              <div className="text-left">
                <p className="font-bold text-lg">Easy Extension!</p>
                <p className="text-blue-100 text-sm">
                  Purchase any plan and the days will be <span className="font-bold">added</span> to your existing {subscriptionDetails.daysRemaining} days.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 md:p-8">
          {/* Discount Code Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Ticket size={20} className="text-purple-600" weight="duotone" />
              <span className="font-semibold text-purple-800">Have a discount code?</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex gap-2 flex-1">
                <Input
                  type="text"
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    if (appliedDiscount) {
                      setAppliedDiscount(null);
                    }
                    setDiscountError('');
                  }}
                  className="flex-1 uppercase"
                  data-testid="discount-code-input"
                />
                <select
                  value={selectedPlanForDiscount}
                  onChange={(e) => {
                    setSelectedPlanForDiscount(e.target.value);
                    if (appliedDiscount) {
                      setAppliedDiscount(null);
                    }
                  }}
                  className="px-3 py-2 border border-purple-200 rounded-lg text-sm bg-white"
                  data-testid="discount-plan-selector"
                >
                  <option value="lite_monthly">Lite</option>
                  <option value="monthly">Monthly</option>
                  <option value="half_yearly">6 Month</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => validateDiscountCode(selectedPlanForDiscount)}
                disabled={validatingCode || !discountCode.trim()}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="apply-discount-btn"
              >
                {validatingCode ? (
                  <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                ) : (
                  <>
                    <Tag size={16} className="mr-1" />
                    Apply
                  </>
                )}
              </Button>
            </div>
            {discountError && (
              <p className="text-red-500 text-sm mt-2">{discountError}</p>
            )}
            {appliedDiscount && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check size={18} className="text-green-600" weight="bold" />
                    <span className="text-green-800 font-medium">
                      Code &quot;{appliedDiscount.code}&quot; applied!
                    </span>
                  </div>
                  <span className="text-green-600 font-bold">{appliedDiscount.discount_display} off</span>
                </div>
                {appliedDiscount.referrer_name && (
                  <p className="text-green-700 text-xs mt-1">Referred by: {appliedDiscount.referrer_name}</p>
                )}
                <p className="text-green-700 text-xs mt-1">
                  Final price for {selectedPlanForDiscount}: {appliedDiscount.final_price_display}
                </p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${
                  plan.badge 
                    ? 'border-amber-400 shadow-lg shadow-amber-100' 
                    : 'border-gray-200'
                } bg-white overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1`}
              >
                {/* Badges (MOST POPULAR, BEST VALUE) */}
                {plan.badge && (
                  <div className={`absolute top-0 left-0 right-0 ${plan.badgeColor} text-white text-xs font-bold text-center py-1.5`}>
                    {plan.badge}
                  </div>
                )}

                <div className={`p-6 ${plan.badge ? 'pt-10' : ''}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                  
                  <div className="mb-4">
                    {/* Pricing — shows launch-offer discounted first payment when active */}
                    {(offerOneTime || launchOffer?.plans?.[plan.id]?.configured) ? (
                      <>
                        <div className="flex items-baseline gap-1 flex-wrap">
                          <span className="text-4xl font-bold text-[#9C302B]">₹{Math.round(plan.price * (100 - offerPct) / 100).toLocaleString()}</span>
                          <span className="text-gray-400 line-through text-lg ml-1">₹{plan.displayPrice}</span>
                        </div>
                        <p className="text-xs font-semibold text-emerald-600 mt-1">{offerPct}% OFF first payment · then ₹{plan.displayPrice}{plan.period}</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-gray-900">₹{plan.displayPrice}</span>
                          <span className="text-gray-500">{plan.period}</span>
                        </div>
                        {plan.monthlyEquivalent && (
                          <p className="text-sm text-gray-500 mt-1">{plan.monthlyEquivalent}</p>
                        )}
                      </>
                    )}
                    {plan.savings && (
                      <span className="inline-block mt-2 text-sm font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        {plan.savings}
                      </span>
                    )}
                    {/* Show what happens: mandate plans auto-renew, others add days */}
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-700 font-medium">
                        {isMandatePlan(plan.id)
                          ? 'Auto-renews · cancel anytime'
                          : `+${plan.id === 'half_yearly' ? '180' : '365'} days will be added`}
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check 
                          size={20} 
                          weight="bold" 
                          className={feature.promo ? 'text-red-500 flex-shrink-0' : feature.highlight ? 'text-amber-500 flex-shrink-0' : 'text-green-500 flex-shrink-0'} 
                        />
                        <span className={`text-sm ${feature.promo ? 'text-red-600 font-bold' : feature.highlight ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}>
                          {feature.text}
                          {feature.promo && <span className="ml-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">2X!</span>}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Simple Buy Button - Anyone can buy any plan */}
                  <Button
                    onClick={() => handleSubscribe(plan)}
                    disabled={processing === plan.id}
                    className={`w-full py-5 ${plan.buttonColor} text-white font-semibold`}
                  >
                    {processing === plan.id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <>
                        <Lightning size={18} className="mr-1" weight="fill" />
                        {subscriptionDetails ? `Add ${plan.id === 'monthly' ? '30' : plan.id === 'half_yearly' ? '180' : '365'} Days` : `Get ${plan.name}`}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              Secure payment via Razorpay. Days are added to your existing subscription.
            </p>
            <p className="text-gray-400 text-xs mt-2">
              Report credits carry forward for the duration of your plan.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
